<template>
  <div class="quantity-input-wrapper" :class="{ multiKey }" data-test="select-wrapper">
    <button
      :disabled="disableDecreaseButton"
      :title="
        currentValue > min
          ? 'Réduire la quantité'
          : 'Le prix à l’unité indiqué ne s’applique pas en dessous de cette quantité.'
      "
      data-test="decrease-button"
      @click="decrease"
    >
      <svg viewBox="-4 -4 16 16"><path d="M0 4h8" /></svg>
    </button>
    <span class="select-wrapper" :class="{ 'quantity-input--with-tooltip': tooltipText }" data-test="tooltip-container">
      <select
        v-model="currentValue"
        aria-label="Quantité"
        title="Sélectionner la quantité"
        :disabled="currentDisabled"
        @keydown.prevent.up="increase"
        @keydown.prevent.down="decrease"
      >
        <option v-for="(_, i) of steps" :key="i + 1" :value="i * step + min">
          {{ displayQuantity(i * step + min) }}{{ displayUnit(i * step + min) }}
          <template v-if="canExtend(i)">et +</template>
        </option>
      </select>
    </span>
    <button
      :disabled="currentValue + step > max || disabled"
      :title="currentValue < max ? 'Augmenter la quantité' : 'Vous avez sélectionné la quantité maximum.'"
      :class="{ 'quantity-input--with-tooltip': isMaxReached && tooltipText }"
      data-test="increase-button"
      @click="increase"
    >
      <svg viewBox="-4 -4 16 16"><path d="M0 4h8zM4 0v8" /></svg>
    </button>
    <figure v-if="tooltipText" class="quantity-input__tooltip">
      <p>{{ tooltipText }}</p>
    </figure>
  </div>
</template>

<script>
import { computed, defineComponent, ref, watch, onMounted, onBeforeUnmount } from 'vue'
import { number } from '../../utils/agreement'

const MAX_SELECT_STEP = 500
const INITIAL_SELECT_STEP = 100
const formatter = new Intl.NumberFormat('fr', { maximumFractionDigits: 5 })
const MAX_REACHED_MESSAGE = 'Quantité maximale atteinte'

export default defineComponent({
  name: 'AdnQuantityInput',
  props: {
    value: {
      type: Number,
    },
    modelValue: {
      type: Number,
    },
    min: {
      type: Number,
      default: 1,
    },
    max: {
      type: Number,
      default: Infinity,
    },
    step: {
      type: Number,
      default: 1,
    },
    unit: {
      default: '',
    },
    multiplier: {
      type: Number,
      default: 1,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    showTooltip: {
      type: Boolean,
      default: false,
    },
    tooltipMessage: {
      type: String,
      default: '',
    },
  },
  setup(props, context) {
    const currentValue = ref(props.value || props.modelValue || props.min)
    const multiKey = ref(false)

    function showMulti(event) {
      multiKey.value =
        (event.type === 'keydown' && (event.key === 'Shift' || event.key === 'Alt')) || event.shiftKey || event.altKey
    }
    onMounted(() => {
      window.addEventListener('keydown', showMulti)
      window.addEventListener('keyup', showMulti)
    })
    onBeforeUnmount(() => {
      window.removeEventListener('keydown', showMulti)
      window.removeEventListener('keyup', showMulti)
    })

    // the maximum possible in constraint of stock or quota
    const maxSteps = computed(() => Math.floor((props.max - props.min) / props.step))

    const currentMaxStepComputedWithSetValue = computed(() => {
      if (Math.ceil(currentValue.value / props.step) < INITIAL_SELECT_STEP) return INITIAL_SELECT_STEP
      return (Math.floor(Math.ceil(currentValue.value / props.step) / MAX_SELECT_STEP) + 1) * MAX_SELECT_STEP
    })

    const isMaxReached = computed(() => currentValue.value + props.step > props.max && props.showTooltip)

    const tooltipText = computed(() => {
      if (!props.showTooltip) return null
      return isMaxReached.value ? MAX_REACHED_MESSAGE : props.tooltipMessage || null
    })

    const selectLength = ref(Math.min(currentMaxStepComputedWithSetValue.value - 1, maxSteps.value))

    const currentMaxValue = computed(() => {
      if (props.max <= props.min) return props.min
      return props.min + props.step * selectLength.value
    })

    const steps = computed(() => {
      if (props.max <= props.min) return 1
      return selectLength.value + 1
    })
    const currentDisabled = computed(() => {
      return props.disabled || props.value > props.max || props.modelValue > props.max
    })
    const disableDecreaseButton = computed(() => {
      return currentDisabled.value || currentValue.value <= props.min
    })

    function displayUnit(n) {
      if (!props.unit) return ''
      const unitDef = typeof props.unit === 'string' ? { other: props.unit } : props.unit
      return `\u2009${number(unitDef, n)}`
    }
    function displayQuantity(q) {
      return formatter.format(q * props.multiplier)
    }
    function decrease(event = {}) {
      let multi = 1
      if (event.shiftKey) multi *= 5
      if (event.altKey) multi *= 5
      if (currentValue.value - props.step * multi <= props.min) currentValue.value = props.min
      else currentValue.value = Math.max(props.min, currentValue.value - props.step * multi)
    }
    function increase(event = {}) {
      let multi = 1
      if (event.shiftKey) multi *= 5
      if (event.altKey) multi *= 5
      if (currentValue.value === 0) currentValue.value = props.min
      else currentValue.value = Math.min(props.max, currentValue.value + props.step * multi)
    }
    function extendSelectLength() {
      if (currentValue.value <= props.max) {
        const stepDifference = maxSteps.value - steps.value + 1

        if (selectLength.value <= INITIAL_SELECT_STEP) {
          selectLength.value += Math.min(stepDifference, MAX_SELECT_STEP - INITIAL_SELECT_STEP)
        } else {
          selectLength.value += Math.min(stepDifference, MAX_SELECT_STEP)
        }
      }
    }
    function canExtend(index) {
      const isCurrentMaximumStep = index === steps.value - 1
      const isNotMaximumValue = index * props.step + props.min !== props.max
      const canHaveAtLeastOneMoreStep = (index + 1) * props.step + props.min <= props.max

      return isCurrentMaximumStep && isNotMaximumValue && canHaveAtLeastOneMoreStep
    }

    watch(currentValue, (newVal) => {
      /**
       * 'input' event. The input value is emited when the value is changed
       *
       * @event input
       * @type {Number}
       */
      context.emit('input', newVal)
      context.emit('update:modelValue', newVal)

      if (canExtend(Math.floor((newVal - props.min) / props.step))) {
        const extendCount = steps.value < MAX_SELECT_STEP ? 1 : Math.floor(steps.value / MAX_SELECT_STEP) + 1
        context.emit('options-extended', extendCount)
        extendSelectLength()
      }
    })

    watch(
      () => props.value,
      (newValue) => {
        if (newValue !== currentValue.value) currentValue.value = newValue
      },
    )

    watch(
      () => props.modelValue,
      (newValue) => {
        if (newValue !== currentValue.value) currentValue.value = newValue
      },
    )

    watch(
      () => props.min,
      (newMin, oldMin) => {
        if (newMin > oldMin && currentValue.value < newMin) currentValue.value = newMin
      },
    )

    watch([currentMaxStepComputedWithSetValue, maxSteps], ([newMaxBetween, newMaxSteps]) => {
      selectLength.value = Math.min(newMaxBetween - 1, newMaxSteps)
    })

    return {
      currentValue,
      currentMaxValue,
      steps,
      disableDecreaseButton,
      currentDisabled,
      tooltipText,
      isMaxReached,
      displayUnit,
      displayQuantity,
      decrease,
      increase,
      extendSelectLength,
      canExtend,
      selectLength,
      multiKey,
    }
  },
})
</script>

<style scoped lang="scss">
div {
  display: flex;
  justify-content: space-between;
  flex-wrap: nowrap;
  width: 100%;
  align-items: center;
  position: relative;

  @media screen and (min-width: $breakpoint-lg) {
    display: inline-block;
    white-space: nowrap;
    justify-content: unset;
    width: unset;
    align-items: unset;
  }

  &.quantity-input-wrapper {
    display: flex;
    column-gap: var(--spacing-1);
  }
}

.select-wrapper {
  flex: 1;
  display: flex;
}

.quantity-input--with-tooltip {
  & ~ .quantity-input__tooltip {
    position: absolute;
    bottom: calc(100% + 1.6rem);
    left: 50%;
    transform: translateX(-50%);
    background-color: var(--color-grey-dark);
    color: var(--color-white);
    padding: var(--spacing-1) var(--spacing-2);
    font-size: var(--font-size-s);
    border-radius: var(--border-radius-1);
    line-height: var(--line-height-m);
    opacity: 0;
    width: 100%;
    text-align: center;
    margin: 0;

    &::after {
      content: '';
      bottom: -1rem;
      border-bottom: 0.6rem solid var(--color-grey-dark);
      border-right: 0.6rem solid var(--color-grey-dark);
      border-top: 0.6rem solid transparent;
      border-left: 0.6rem solid transparent;
      position: absolute;
      left: 50%;
      transform: translateX(-50%) translateY(-50%) rotate(45deg);
    }
  }

  &:hover ~ .quantity-input__tooltip {
    transition-duration: 0.8s;
    opacity: 1;
  }

  @media screen and (min-width: $breakpoint-sm) {
    & ~ .quantity-input__tooltip {
      width: auto;
    }
  }
}

select {
  appearance: none;
  background: #fff;
  box-sizing: border-box;
  border-radius: var(--border-radius-1);
  border-color: var(--Border-colors-neutral-400, #dee3e6) !important;
  color: var(--Text-colors-neutral-600, #566771) !important;
  flex: 1;
  height: var(--line-height-3xl);
  font-size: inherit;
  padding: 0 10px;
  text-align: center;
  background-image: url('@b2ag/themes/aladin/assets/chevron-down.svg');
  background-repeat: no-repeat;
  background-position-x: calc(100% - 0.6rem);
  background-position-y: 50%;
  padding-right: 2rem;

  &:hover {
    border-radius: var(--Corner-radius-radius-s, 4px) !important;
    border: 2px solid var(--Border-colors-neutral-400, #dee3e6) !important;
  }

  @media screen and (min-width: $breakpoint-lg) {
    min-width: 100px;
  }
}

button {
  background-color: var(--color-white);
  border-radius: var(--border-radius-1);
  border: 1px solid var(--Border-colors-neutral-400, #dee3e6) !important;
  box-sizing: border-box;
  color: var(--Text-colors-neutral-600, #566771) !important;
  height: var(--line-height-3xl);
  line-height: var(--line-height-m);
  transition: all 0.1s ease-in;
  width: var(--line-height-3xl);
  padding: 0;

  > svg {
    width: var(--line-height-m);
    height: var(--line-height-m);
    stroke: currentcolor;
    stroke-width: 1.333px;
    fill: none;
    vertical-align: top;
  }

  &:hover:not(:disabled) {
    border-radius: var(--Corner-radius-radius-s, 4px) !important;
    border: 2px solid var(--Border-colors-neutral-400, #dee3e6) !important;
  }

  &:active:not(:disabled) {
    border-radius: var(--Corner-radius-radius-s, 4px) !important;
    border: 1px solid var(--Border-colors-neutral-700, #00163e) !important;
  }

  &:disabled {
    box-shadow: none;
    border-color: var(--color-grey-light) !important;
    color: var(--color-inactive-light) !important;
  }
}
</style>
